import { Box, Typography, useTheme } from '@mui/material';
import {
  GridTreeDataGroupingCell,
  GridChartsIntegrationContextProvider,
  GridChartsRendererProxy,
  isAutogeneratedRow,
  type GridChartsPanelProps,
  type DataGridPremiumProps,
  type GridGroupNode,
  type GridRowSelectionModel,
} from '@mui/x-data-grid-premium';
import {
  ChartsRenderer,
  configurationOptions,
  type ChartsRendererProps,
} from '@mui/x-charts-premium/ChartsRenderer';
import { useCallback, useMemo } from 'react';
import {
  v2DataGridFocusStyles,
  v2DataGridStyles,
} from '../Families/v2DataGridStyles';
import { TestFamilyBadge } from '../Families/TestFamilyBadge';
import { v2Typography } from '../Families/v2Typography';
import { AgeText } from '../Families/AgeText';
import {
  buildVolunteersGridColumns,
  VOLUNTEERS_SEARCH_FIELD,
} from './volunteersGridColumns';
import type { VolunteerBrowserRowV2 } from './useVolunteersBrowserViewModel';
import { VolunteersChartsPanel } from './VolunteersChartsPanel';
import { usePersistedGridFilterModel } from '../Hooks/usePersistedGridFilterModel';
import { PersistedFilterDataGridPremium } from '../Generic/PersistedFilterDataGridPremium';
import { notAppliedRoleFilterValue } from './roleFilterValues';
import { simplify } from '../Utilities/stringUtils';

type Props = {
  arrangementTypes: string[];
  familyCustomFields: Parameters<
    typeof buildVolunteersGridColumns
  >[0]['familyCustomFields'];
  loading?: boolean;
  onRowClick: (row: VolunteerBrowserRowV2) => void;
  onRowSelectionModelChange: (model: GridRowSelectionModel) => void;
  onPivotActiveChange: (active: boolean) => void;
  roleNames: string[];
  rowSelectionModel: GridRowSelectionModel;
  rows: VolunteerBrowserRowV2[];
  updateTestFamilyFlagEnabled?: boolean;
  pivotActive: boolean;
  volunteerCustomFields: Parameters<
    typeof buildVolunteersGridColumns
  >[0]['volunteerCustomFields'];
};

const getVolunteerTreePath = (row: VolunteerBrowserRowV2) => row.treePath;

export function VolunteersDataGridV2({
  arrangementTypes,
  familyCustomFields,
  loading = false,
  onRowClick,
  onRowSelectionModelChange,
  onPivotActiveChange,
  roleNames,
  rowSelectionModel,
  rows,
  updateTestFamilyFlagEnabled,
  pivotActive,
  volunteerCustomFields,
}: Props) {
  const theme = useTheme();
  const columns = useMemo(
    () =>
      buildVolunteersGridColumns({
        arrangementTypes,
        familyCustomFields,
        rows,
        roleNames,
        updateTestFamilyFlagEnabled,
        volunteerCustomFields,
      }),
    [
      arrangementTypes,
      familyCustomFields,
      roleNames,
      rows,
      updateTestFamilyFlagEnabled,
      volunteerCustomFields,
    ]
  );
  const treeRows = useMemo(
    () =>
      new Map(
        rows.map((row) => [row.treePath[row.treePath.length - 1], row])
      ),
    [rows]
  );
  const groupingColDef = useMemo<
    DataGridPremiumProps<VolunteerBrowserRowV2>['groupingColDef']
  >(
    () => ({
      headerName: 'Family',
      hideable: false,
      minWidth: 240,
      flex: 1,
      valueFormatter: (value) => {
        const row = treeRows.get(String(value));
        return row?.rowKind === 'family'
          ? row.family
          : (row?.personName ?? '');
      },
      getApplyQuickFilterFn: (value) => {
        const query = simplify(String(value));
        return (cell) => {
          const row = treeRows.get(String(cell));
          if (!row) return false;
          const searchable =
            row.rowKind === 'family'
              ? [row.family, row.primaryContact, row.searchableText]
              : [row.personName];
          return simplify(searchable.filter(Boolean).join(' ')).includes(query);
        };
      },
      renderCell: (params) => (
        <GridTreeDataGroupingCell
          {...params}
          hideDescendantCount
          rowNode={params.rowNode as GridGroupNode}
          formattedValue={
            params.row.rowKind === 'family' ? (
              <Box sx={{ alignItems: 'center', display: 'flex', minWidth: 0 }}>
                <Typography noWrap {...v2Typography.browserCell}>
                  {params.row.family || '-'}
                </Typography>
                {updateTestFamilyFlagEnabled && (
                  <TestFamilyBadge family={params.row.sourceFamily} />
                )}
              </Box>
            ) : (
              <>
                {params.row.personName}
                {params.row.personAge && (
                  <>
                    {' '}
                    <em>
                      <small>
                        (<AgeText age={params.row.personAge} />)
                      </small>
                    </em>
                  </>
                )}
              </>
            )
          }
        />
      ),
    }),
    [treeRows, updateTestFamilyFlagEnabled]
  );
  const columnVisibilityModel = useMemo(
    () =>
      Object.fromEntries([
        [VOLUNTEERS_SEARCH_FIELD, false],
        ['volunteerFamilyCount', false],
        ...arrangementTypes.map((type) => [
          `arrangementAssignment:${type}`,
          false,
        ]),
        ...roleNames
          .filter((roleName) => roleName !== notAppliedRoleFilterValue)
          .map((roleName) => [`roleStatus:${roleName}`, false]),
        ...familyCustomFields.map((field) => [
          `familyCustomField:${field.name}`,
          false,
        ]),
        ...volunteerCustomFields.map((field) => [
          `volunteerCustomField:${field.name}`,
          false,
        ]),
      ]),
    [arrangementTypes, familyCustomFields, roleNames, volunteerCustomFields]
  );
  const visibleColumns = useMemo(
    () => ({ ...columnVisibilityModel, family: pivotActive }),
    [columnVisibilityModel, pivotActive]
  );
  const gridConfigurationKey = useMemo(
    () =>
      JSON.stringify([
        rows.length === 0,
        columns.map(({ chartable, field, pivotable, type }) => [
          field,
          type,
          pivotable,
          chartable,
        ]),
      ]),
    [columns, rows.length]
  );
  const slots = useMemo(
    () => ({
      chartsPanel: (props: GridChartsPanelProps) => (
        <VolunteersChartsPanel {...props} pivotActive={pivotActive} />
      ),
    }),
    [pivotActive]
  );
  const renderChart = useCallback(
    (props: ChartsRendererProps) =>
      pivotActive && props.chartType ? (
        <Box sx={{ minWidth: 0, overflow: 'auto', width: '100%' }}>
          <ChartsRenderer {...props} />
        </Box>
      ) : null,
    [pivotActive]
  );
  const { filterModel, filterModelRestored, onFilterModelChange } =
    usePersistedGridFilterModel({
      columns,
      namespace: 'volunteers',
    });

  return (
    <GridChartsIntegrationContextProvider key={gridConfigurationKey}>
      <Box
        sx={{
          ...v2DataGridStyles(theme, { height: '100%' }),
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        <PersistedFilterDataGridPremium
          key={pivotActive ? 'volunteers-pivot-grid' : 'volunteers-tree-grid'}
          showToolbar
          chartsIntegration
          treeData={!pivotActive}
          getTreeDataPath={getVolunteerTreePath}
          groupingColDef={pivotActive ? undefined : groupingColDef}
          disableChildrenSorting
          pivotActive={pivotActive}
          onPivotActiveChange={onPivotActiveChange}
          aggregationRowsScope="filtered"
          pagination={pivotActive}
          rows={
            pivotActive ? rows.filter((row) => row.rowKind === 'family') : rows
          }
          columns={columns}
          filterModel={filterModel}
          filterModelRestored={filterModelRestored}
          onFilterModelChange={onFilterModelChange}
          slots={slots}
          loading={loading}
          getEstimatedRowHeight={() => 88}
          getRowHeight={() => 'auto'}
          columnHeaderHeight={42}
          checkboxSelection={!pivotActive}
          disableRowSelectionOnClick
          disableRowSelectionExcludeModel
          isRowSelectable={(params) => params.row.rowKind === 'family'}
          onRowClick={({ row }) => {
            if (!pivotActive && !isAutogeneratedRow(row)) {
              onRowClick(row);
            }
          }}
          onRowSelectionModelChange={onRowSelectionModelChange}
          pageSizeOptions={[25, 50, 100]}
          rowSelectionModel={rowSelectionModel}
          sx={{
            ...v2DataGridFocusStyles(theme),
            minWidth: 0,
            '& .MuiDataGrid-row': { cursor: 'pointer', minHeight: 56 },
            '& .MuiDataGrid-cell': {
              alignItems: 'center',
              display: 'flex',
              py: 1,
            },
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 100 } },
            sorting: { sortModel: [{ field: 'family', sort: 'asc' }] },
            columns: {
              columnVisibilityModel: visibleColumns,
            },
          }}
          slotProps={{
            chartsPanel: {
              schema: {
                bar: configurationOptions.bar,
                column: configurationOptions.column,
                pie: configurationOptions.pie,
              },
            },
            toolbar: {
              quickFilterProps: {
                quickFilterParser: (value: string) => (value ? [value] : []),
              },
            },
            baseCheckbox: {
              slotProps: {
                htmlInput: { title: 'Select families to enable bulk actions.' },
              },
            },
          }}
        />
      </Box>
      <GridChartsRendererProxy
        id="volunteers-report"
        label="Volunteer family counts"
        renderer={renderChart}
      />
    </GridChartsIntegrationContextProvider>
  );
}
